/**
 * MagicLinkPage — exchanges a magic-link JWT for an authenticated session.
 *
 * Flow:
 * 1. Read `token` and `redirect` from URL query params.
 * 2. Validate `redirect` is an internal path (defense in depth — backend
 *    re-checks the binding too).
 * 3. POST to `/auth/magic-link` via the auth context.
 * 4. On success, hard-navigate to `redirect` so the app re-inits with the new
 *    session.
 *
 * A used, expired or invalid token goes to /login with the same redirect (or
 * straight through if already signed in). Errors are rendered for invalid
 * params, service unavailable and anything unexpected.
 */

import { useEffect, useRef, useState } from 'react'
import { useSearch, Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { AuthServiceError } from '@/common/services/auth'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import * as authStyles from './login.css'

export const Route = createFileRoute({
  component: MagicLinkPage,
})

// Reject absolute URLs, protocol-relative paths (`//host/...`), and characters
// that aren't safe in an internal path. The negative lookahead `(?!/)` blocks
// a leading `//` which would otherwise match the rest of the char class and
// be resolved off-site by `window.location.assign`.
const REDIRECT_PATH_PATTERN = /^\/(?!\/)[A-Za-z0-9/_-]+$/

type ExchangeStatus =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

function MagicLinkPage() {
  const { magicLinkLogin } = useAuth()
  const search = useSearch({ strict: false }) as { token?: string; redirect?: string }
  const token = search.token || ''
  const redirect = search.redirect || ''

  const [status, setStatus] = useState<ExchangeStatus>({ kind: 'pending' })
  // Guard against React strict-mode double-invoke for a given token/redirect
  // pair so we never POST a single-use token twice. Keying on the pair (rather
  // than a boolean) lets a user paste a fresh magic link into the same tab —
  // TanStack Router can re-render the route on search-param changes without
  // unmounting, which would otherwise be permanently stuck on the first token.
  const startedRef = useRef<string | null>(null)

  useEffect(() => {
    const exchangeKey = `${token}|${redirect}`
    if (startedRef.current === exchangeKey) return
    startedRef.current = exchangeKey
    setStatus({ kind: 'pending' })

    if (!token || !redirect) {
      setStatus({
        kind: 'error',
        message: 'This magic link is missing required information.',
      })
      return
    }

    if (!REDIRECT_PATH_PATTERN.test(redirect)) {
      setStatus({ kind: 'error', message: 'This magic link is invalid.' })
      return
    }

    const exchange = async () => {
      try {
        await magicLinkLogin(token, redirect)
        setStatus({ kind: 'success' })
        // Hard-navigate so the app fully re-initializes with the new session
        // and no stale React Query cache from a prior tab leaks through.
        window.location.assign(redirect)
      } catch (err) {
        if (err instanceof AuthServiceError) {
          // Used or expired link: send them to sign in (or straight through if they already are) instead of an error
          if (err.status === 409 || err.status === 400) {
            window.location.replace(`/login?redirect=${encodeURIComponent(redirect)}`)
            return
          }
          if (err.status === 503) {
            setStatus({
              kind: 'error',
              message:
                'Our authentication service is temporarily unavailable. Please try again in a moment.',
            })
            return
          }
        }
        setStatus({
          kind: 'error',
          message: 'Something went wrong. Please log in normally to continue.',
        })
      }
    }

    exchange()
  }, [token, redirect, magicLinkLogin])

  return (
    <div className={authStyles.container}>
      <div className={authStyles.card}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Cash Offer"
          className={authStyles.logo}
        />

        {status.kind === 'pending' && (
          <div className={authStyles.form}>
            <Text variant="bodyLg" weight="bold" align="center" className={authStyles.title}>
              Signing you in…
            </Text>
            <Text variant="bodySm" color="muted" align="center" className={authStyles.description}>
              Just a moment while we verify your link.
            </Text>
          </div>
        )}

        {status.kind === 'success' && (
          <div className={authStyles.form}>
            <Text variant="bodyLg" weight="bold" align="center" className={authStyles.title}>
              You're signed in
            </Text>
            <Text variant="bodySm" color="muted" align="center" className={authStyles.description}>
              Redirecting you now…
            </Text>
          </div>
        )}

        {status.kind === 'error' && (
          <div className={authStyles.form}>
            <Text variant="bodyLg" weight="bold" align="center" className={authStyles.title}>
              Sign-in link didn't work
            </Text>
            <Text variant="bodySm" color="error" align="center" className={authStyles.error}>
              {status.message}
            </Text>
            <Link to="/login">
              <Button type="button" width="full">
                Go to Sign In
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
